import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Cart items component with KOTIN PC Protection Plan quantity pairing.
 * A PC row and its paired plan row are updated in one cart/update request.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLTableRowElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Returns the plan row paired to a PC row by pair ID or covered line key.
   * @param {HTMLTableRowElement | undefined} pcRow
   * @returns {HTMLTableRowElement | undefined}
   */
  #getPairedPlanRow(pcRow) {
    if (!pcRow || pcRow.dataset.protectionPlan === 'true') return undefined;

    const pairId = pcRow.dataset.coveragePairId;
    const pcLineKey = pcRow.dataset.key;

    return this.refs.cartItemRows.find((row) => {
      if (row.dataset.protectionPlan !== 'true') return false;
      return Boolean(
        (pairId && row.dataset.coveragePairId === pairId) ||
          (pcLineKey && row.dataset.coveredLineKey === pcLineKey)
      );
    });
  }

  /** @param {QuantitySelectorUpdateEvent} event */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    if (quantity === 0) return this.onLineItemRemove(line);

    this.updateQuantity({ line, quantity, action: 'change' });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const pairedPlanRow = this.#getPairedPlanRow(lineItemRow);
    for (const row of [lineItemRow, pairedPlanRow]) {
      const textComponent = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
      textComponent?.shimmer();
    }
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const pairedPlanRow = this.#getPairedPlanRow(cartItemRowToRemove);
    const rowsToRemove = Array.from(
      new Set([
        cartItemRowToRemove,
        pairedPlanRow,
        ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ].filter(Boolean))
    );

    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const changedRow = this.refs.cartItemRows[line - 1];
    const pairedPlanRow = this.#getPairedPlanRow(changedRow);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    const requestData = pairedPlanRow && changedRow?.dataset.key && pairedPlanRow.dataset.key
      ? {
          updates: {
            [changedRow.dataset.key]: quantity,
            [pairedPlanRow.dataset.key]: quantity,
          },
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        }
      : {
          line,
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        };

    const route = pairedPlanRow
      ? Theme.routes.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`
      : Theme.routes.cart_change_url;

    cartTotal?.shimmer();

    fetch(route, fetchConfig('json', { body: JSON.stringify(requestData) }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);
        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');
    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');
    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
